import React, { useEffect, useMemo, useState } from 'react';
import { Outlet, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { useTheme } from '../ThemeContext';
import { useBranding } from '../BrandingContext';
import { useTranslation } from 'react-i18next';
import {
  LuBot,
  LuCalendar,
  LuChartBar,
  LuChevronDown,
  LuCreditCard,
  LuGift,
  LuLayoutDashboard,
  LuLifeBuoy,
  LuLogOut,
  LuMail,
  LuMenu,
  LuMoon,
  LuPackage,
  LuPaintbrush,
  LuPalette,
  LuServer,
  LuSettings,
  LuShieldCheck,
  LuSun,
  LuTicket,
  LuTrendingUp,
  LuUser,
  LuUsers,
  LuX,
} from 'react-icons/lu';

import './AdminLayout.css';

// --- Компонент: Переключатель темы ---
const ThemeSwitcherImpl = () => {
  const { theme, toggleTheme } = useTheme();
  return (
    <button className="btn-icon" onClick={toggleTheme} title={theme === 'light' ? 'Включить темную' : 'Включить светлую'}>
      {theme === 'light' ? <LuMoon /> : <LuSun />}
    </button>
  );
};

export default function AdminLayout() {
  const { t } = useTranslation();
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login'); 
  };

  // Эффект "scrolled" для топ-навигации
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Закрываем меню при смене маршрута
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setOpenDropdownId(null);
    setIsUserMenuOpen(false);
  }, [location.pathname]);

  // Закрываем dropdown при клике вне
  useEffect(() => {
    const onDocClick = (e) => {
      const target = e.target;
      if (!(target instanceof Element)) return;
      if (target.closest('.nav-category-dropdown') || target.closest('.nav-user-menu')) return;
      setOpenDropdownId(null);
      setIsUserMenuOpen(false);
    };
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, []);

  // Меню по категориям: один пункт — ссылка, несколько — выпадающий список
  const menuCategories = useMemo(
    () => [
      {
        id: 'main',
        label: 'Главное',
        items: [{ path: '/admin/dashboard', icon: LuLayoutDashboard, label: 'Дашборд' }],
      },
      {
        id: 'clients',
        label: 'Клиенты',
        items: [
          { path: '/admin/users', icon: LuUsers, label: 'Пользователи' },
          { path: '/admin/referrals', icon: LuGift, label: 'Рефералы' },
        ],
      },
      {
        id: 'sales',
        label: 'Продажи',
        items: [
          { path: '/admin/tariffs', icon: LuShieldCheck, label: 'Тарифы' },
          { path: '/admin/promocodes', icon: LuTicket, label: 'Промокоды' },
          { path: '/admin/payments', icon: LuCreditCard, label: 'Платежи' },
          { path: '/admin/sales', icon: LuChartBar, label: 'Отчёты' },
          { path: '/admin/analytics', icon: LuTrendingUp, label: 'Аналитика' },
        ],
      },
      {
        id: 'infra',
        label: 'Инфра',
        items: [
          { path: '/admin/squads', icon: LuServer, label: 'Сквады' },
          { path: '/admin/nodes', icon: LuServer, label: 'Ноды' },
        ],
      },
      {
        id: 'support',
        label: 'Поддержка',
        items: [{ path: '/admin/support', icon: LuLifeBuoy, label: 'Поддержка' }],
      },
    ],
    []
  );

  const settingsItems = useMemo(
    () => [
      { path: '/admin/trial-settings', icon: LuCalendar, label: 'Настройки триала' },
      { path: '/admin/tariff-levels', icon: LuShieldCheck, label: 'Уровни тарифов' },
      { path: '/admin/tariff-features', icon: LuShieldCheck, label: 'Функции тарифов' },
      { path: '/admin/options', icon: LuPackage, label: 'Опции' },
      { path: '/admin/system-settings', icon: LuSettings, label: 'Системные настройки' },
      { path: '/admin/branding', icon: LuPalette, label: 'Брендинг' },
      { path: '/admin/email-templates', icon: LuMail, label: 'Шаблоны писем' },
      { path: '/admin/theme', icon: LuPaintbrush, label: 'Стиль панели' },
      { path: '/admin/bot-constructor', icon: LuBot, label: 'Конструктор бота' },
      { path: '/admin/broadcast', icon: LuMail, label: t('broadcast.title') },
    ],
    [t]
  );

  const isCategoryActive = (category) =>
    category.items.some((item) => location.pathname === item.path);
  const isSettingsActive =
    location.pathname.startsWith('/admin/trial-settings') ||
    location.pathname.startsWith('/admin/tariff-levels') ||
    location.pathname.startsWith('/admin/tariff-features') ||
    location.pathname.startsWith('/admin/options') ||
    location.pathname.startsWith('/admin/system-settings') ||
    location.pathname.startsWith('/admin/branding') ||
    location.pathname.startsWith('/admin/email-templates') ||
    location.pathname.startsWith('/admin/theme') ||
    location.pathname.startsWith('/admin/bot-constructor') ||
    location.pathname.startsWith('/admin/broadcast');

  const { branding } = useBranding();
  const logoName = (branding?.site_name || '').trim().toUpperCase() || 'PANEL';

  return (
    <div className="admin-layout admin-layout-v3">
      {/* Верхнее навигационное меню (как в V3) */}
      <nav className={`admin-top-nav ${scrolled ? 'scrolled' : ''}`}>
        <div className="nav-container">
          <div className="nav-brand">
            <div className="logo-text">{logoName}</div>
            <span className="logo-subtitle">Admin Panel</span>
          </div>

          {/* Десктоп-меню по категориям */}
          <div className="nav-menu-desktop">
            {menuCategories.map((category) => {
              const isOpen = openDropdownId === category.id;
              const isActive = isCategoryActive(category);
                if (category.items.length === 1) {
                const item = category.items[0];
                const Icon = item.icon;
                const itemActive = location.pathname === item.path;
                return (
                  <NavLink
                    key={category.id}
                    to={item.path}
                    className={`nav-item ${itemActive ? 'active' : ''}`}
                    title={category.label}
                  >
                    <Icon className="nav-icon" />
                    <span className="nav-label">{category.label}</span>
                  </NavLink>
                );
              }
              const firstItem = category.items[0];
              const FirstIcon = firstItem.icon;
              return (
                <div key={category.id} className="nav-category-dropdown">
                  <button
                    type="button"
                    className={`nav-item dropdown-toggle ${isActive ? 'active' : ''}`}
                    title={category.label}
                    onClick={() => {
                      setOpenDropdownId((v) => (v === category.id ? null : category.id));
                      setIsUserMenuOpen(false);
                    }}
                  >
                    <FirstIcon className="nav-icon" />
                    <span className="nav-label">{category.label}</span>
                    <LuChevronDown className={`dropdown-arrow ${isOpen ? 'open' : ''}`} />
                  </button>
                  {isOpen && (
                    <div className="dropdown-menu category-dropdown-menu">
                      {category.items.map((item) => {
                        const Icon = item.icon;
                        const itemActive = location.pathname === item.path;
                        return (
                          <NavLink
                            key={item.path}
                            to={item.path}
                            className={`dropdown-item ${itemActive ? 'active' : ''}`}
                            onClick={() => setOpenDropdownId(null)}
                          >
                            <Icon className="dropdown-icon" />
                            <span>{item.label}</span>
                          </NavLink>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}

            {/* Выпадающее меню настроек */}
            <div className="nav-category-dropdown">
              <button
                type="button"
                className={`nav-item dropdown-toggle ${isSettingsActive ? 'active' : ''}`}
                title="Настройки"
                onClick={() => {
                  setOpenDropdownId((v) => (v === 'settings' ? null : 'settings'));
                  setIsUserMenuOpen(false);
                }}
              >
                <LuSettings className="nav-icon" />
                <span className="nav-label">Настройки</span>
                <LuChevronDown className={`dropdown-arrow ${openDropdownId === 'settings' ? 'open' : ''}`} />
              </button>
              {openDropdownId === 'settings' && (
                <div className="dropdown-menu category-dropdown-menu">
                  {settingsItems.map((item) => {
                    const Icon = item.icon;
                    const isActive = location.pathname === item.path;
                    return (
                      <NavLink
                        key={item.path}
                        to={item.path}
                        className={`dropdown-item ${isActive ? 'active' : ''}`}
                        onClick={() => setOpenDropdownId(null)}
                      >
                        <Icon className="dropdown-icon" />
                        <span>{item.label}</span>
                      </NavLink>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Правая часть */}
          <div className="nav-actions">
            <ThemeSwitcherImpl />

            <div className="nav-user-menu">
              <button
                className="nav-user-btn"
                onClick={() => {
                  setIsUserMenuOpen((v) => !v);
                  setOpenDropdownId(null);
                }}
                type="button"
              >
                <LuUser className="user-icon" />
                <span className="user-email">{user?.email || 'Admin'}</span>
                <LuChevronDown className={`dropdown-arrow ${isUserMenuOpen ? 'open' : ''}`} />
              </button>
              {isUserMenuOpen && (
                <div className="user-dropdown-menu">
                  <div className="user-dropdown-header">
                    <div className="user-info">
                      <div className="user-name">{user?.email || 'Admin'}</div>
                      <div className="user-role">Администратор</div>
                    </div>
                  </div>
                  <button onClick={handleLogout} className="user-dropdown-item logout" type="button">
                    <LuLogOut />
                    <span>Выйти</span>
                  </button>
                </div>
              )}
            </div>

            {/* Мобильное меню */}
            <button
              className="mobile-menu-toggle"
              onClick={() => setIsMobileMenuOpen((v) => !v)}
              aria-label="Меню"
              type="button"
            >
              {isMobileMenuOpen ? <LuX /> : <LuMenu />}
            </button>
          </div>
        </div>

        {/* Мобильное выпадающее меню по категориям */}
        {isMobileMenuOpen && (
          <>
            <div className="mobile-menu-overlay" onClick={() => setIsMobileMenuOpen(false)} aria-hidden="true" />
            <div className="nav-menu-mobile" role="navigation" aria-label="Меню">
              {menuCategories.map((category) => (
                <div key={category.id} className="mobile-menu-section">
                  <div className="mobile-section-title">{category.label}</div>
                  {category.items.map((item) => {
                    const Icon = item.icon;
                    const isActive = location.pathname === item.path;
                    return (
                      <NavLink
                        key={item.path}
                        to={item.path}
                        className={`nav-item-mobile ${isActive ? 'active' : ''}`}
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        <Icon className="nav-item-mobile-icon" />
                        <span>{item.label}</span>
                      </NavLink>
                    );
                  })}
                </div>
              ))}

              <div className="mobile-menu-section">
                <div className="mobile-section-title">Настройки</div>
                {settingsItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.path;
                  return (
                    <NavLink
                      key={item.path}
                      to={item.path}
                      className={`nav-item-mobile ${isActive ? 'active' : ''}`}
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <Icon className="nav-item-mobile-icon" />
                      <span>{item.label}</span>
                    </NavLink>
                  );
                })}
              </div>

              <button onClick={handleLogout} className="nav-item-mobile logout" type="button">
                <LuLogOut className="nav-item-mobile-icon" />
                <span>Выйти</span>
              </button>
            </div>
          </>
        )}
      </nav>

      {/* Контент страницы */}
      <main className="admin-main-content">
        <div className="admin-page-wrapper">
          <Outlet /> 
        </div>
      </main>
    </div>
  );
}